/**
 * Persistance de la conversation côté navigateur.
 *
 * Une seule clé localStorage `studiof-agents:conversation:[identity]` qui
 * contient un objet unique avec session_id, conversation_id, token et un
 * timestamp de création. Une seule durée de vie (TTL_MS) gouverne le tout :
 * passé le délai, l'entrée est invalidée et une nouvelle session_id est
 * générée à la volée. Cela évite le bug confusant où le visiteur voyait un
 * chat vide alors que le serveur retrouvait sa conversation d'hier via un
 * vieux session_id encore présent en local.
 *
 * conversation_id et token ne sont peuplés qu'après le premier échange avec
 * le serveur (frame SSE `meta`). En attendant, ils restent null.
 *
 * Pourquoi localStorage et pas sessionStorage : le visiteur navigue d'une
 * page à l'autre sur le site du client et la conversation doit se poursuivre.
 * sessionStorage est isolé par tab, localStorage est isolé par origine du
 * site hôte (donc par client), ce qui est exactement ce qu'on veut.
 */

const STORAGE_PREFIX = 'studiof-agents:conversation:';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h, constante non exposée à l'admin

export type StoredConversation = {
  session_id: string;
  conversation_id: string | null;
  token: string | null;
  created_at: number; // epoch ms
};

function isStoredConversation(value: unknown): value is StoredConversation {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.session_id === 'string' &&
    (v.conversation_id === null || typeof v.conversation_id === 'string') &&
    (v.token === null || typeof v.token === 'string') &&
    typeof v.created_at === 'number'
  );
}

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function freshConversation(): StoredConversation {
  return {
    session_id: generateSessionId(),
    conversation_id: null,
    token: null,
    created_at: Date.now(),
  };
}

/**
 * Récupère l'entrée de conversation pour cet agent. Si elle n'existe pas, est
 * malformée ou a dépassé le TTL, on en génère une nouvelle (avec un nouveau
 * session_id) et on l'écrit immédiatement.
 *
 * Cas dégradé : si localStorage est indisponible (Safari privé, quota plein),
 * on retourne une entrée éphémère qui ne sera jamais persistée. Le widget
 * fonctionne, simplement la conversation repartira de zéro à chaque page.
 */
export function getOrCreateConversation(identity: string): StoredConversation {
  const key = `${STORAGE_PREFIX}${identity}`;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (isStoredConversation(parsed) && Date.now() - parsed.created_at < TTL_MS) {
        return parsed;
      }
      // Expirée ou corrompue : on purge avant d'en regénérer une
      window.localStorage.removeItem(key);
    }
    const fresh = freshConversation();
    window.localStorage.setItem(key, JSON.stringify(fresh));
    return fresh;
  } catch {
    return freshConversation();
  }
}

/**
 * Met à jour l'entrée de conversation, par exemple après réception du frame
 * SSE meta avec conversation_id et token.
 */
export function updateStoredConversation(
  identity: string,
  patch: Partial<Pick<StoredConversation, 'conversation_id' | 'token'>>,
): void {
  const key = `${STORAGE_PREFIX}${identity}`;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!isStoredConversation(parsed)) return;
    const merged: StoredConversation = {
      ...parsed,
      ...patch,
    };
    window.localStorage.setItem(key, JSON.stringify(merged));
  } catch {
    // ignore
  }
}

/**
 * Purge l'entrée pour cet agent et recrée une nouvelle session vierge.
 * Utilisé par le bouton « Nouvelle conversation » du widget.
 */
export function resetConversation(identity: string): StoredConversation {
  const key = `${STORAGE_PREFIX}${identity}`;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
  return getOrCreateConversation(identity);
}

const OPEN_STATE_KEY = 'studiof-agents:open';

export function getStoredOpenState(): boolean {
  try {
    return window.sessionStorage.getItem(OPEN_STATE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setStoredOpenState(isOpen: boolean): void {
  try {
    if (isOpen) {
      window.sessionStorage.setItem(OPEN_STATE_KEY, '1');
    } else {
      window.sessionStorage.removeItem(OPEN_STATE_KEY);
    }
  } catch {
    // ignore
  }
}
